import { AutomatedContentBanner } from "@2u-landing-pages/components/AutomatedContent";
import { Button } from "@2u-landing-pages/components/Button";
import { VideoEmbed } from "@2u-landing-pages/components/VideoEmbed";
import { usePageState } from "@2u-landing-pages/providers";
import {
  TypeAccordionWithoutUnresolvableLinksResponse,
  TypeAutomated_contentWithoutUnresolvableLinksResponse,
  TypeBannerWithoutUnresolvableLinksResponse,
  TypeBentoWithoutUnresolvableLinksResponse,
  TypeCarouselWithoutUnresolvableLinksResponse,
  TypeFlexWithoutUnresolvableLinksResponse,
  TypeFormWithoutUnresolvableLinksResponse,
  TypeHeroWithoutUnresolvableLinksResponse,
  TypeSplitWithoutUnresolvableLinksResponse,
  TypeTabsWithoutUnresolvableLinksResponse,
  TypeVideoWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useMediaQuery } from "@2u-landing-pages/utils/useMediaQuery";
import { Drawer as BricksDrawer, ButtonGroup, Container } from "@red-digital/bricks";
import cn from "classnames";
import React, { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { Accordion } from "../../../Accordion";
import { Banner } from "../../../Banner";
import { Bento } from "../../../Bento";
import { Carousel } from "../../../Carousel";
import { Flex } from "../../../Flex";
import { Form } from "../../../Form";
import { Hero } from "../../../Hero";
import { Split } from "../../../Split";
import { Tabs } from "../../../Tabs";
import { DrawerProps } from "../../Drawer.types";

// @ts-ignore
import styles from "./SideDrawer.module.scss";

const BANNER_OFFSET_PX = "52px";

type SideDrawerContentEntry = NonNullable<NonNullable<DrawerProps["fields"]>["content"]>[number];

function getFirstContentEntrySurfaceToken(entry: SideDrawerContentEntry | undefined): string | undefined {
  if (!entry?.fields) return undefined;
  const typeId = entry.sys?.contentType?.sys?.id;
  const f = entry.fields as Record<string, string | undefined>;
  switch (typeId) {
    case "form":
      return f.section_background_color;
    case "banner":
      return f.background_color;
    case "carousel":
    case "split":
    case "bento":
    case "flex":
    case "accordion":
      return f.section_background_color;
    case "tabs":
      return f.tabs_section_background;
    default:
      return undefined;
  }
}

const MODAL_LOCATION = "Modal";
const MODAL_CLOSE_ELEMENT_TYPE = "Modal Close";
const MODAL_ELEMENT_TYPE = "Modal";
const MODAL_VIEWED_INTERSECTION_THRESHOLD = 0.5;

export const SideDrawer: React.FC<DrawerProps> = ({ fields, inspector = () => null, isLoading, phoneNumber, children, programVariationName }) => {
  const { state, dispatch } = usePageState();
  const { closeButton, banner, content, auto_open_drawer } = fields || {};

  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const hasContent = Boolean(children || (content && content.length > 0));

  const drawerPosition = isDesktop ? "right" : "bottom";
  const drawerLocation = isDesktop ? "Side Drawer" : "Drawer";

  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const hasFiredElementLoaded = useRef(false);

  useEffect(() => {
    if (hasFiredElementLoaded.current || !hasContent) return;
    hasFiredElementLoaded.current = true;
    EventClient.elementLoaded({
      webElement: {
        location: MODAL_LOCATION,
        name: programVariationName,
      },
    });
  }, [hasContent, programVariationName]);

  useEffect(() => {
    if (!state.isDrawerOpen || !hasContent) return;
    const target = drawerContentRef.current;
    if (!target || typeof IntersectionObserver === "undefined") return;

    let hasFired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasFired) {
            hasFired = true;
            EventClient.elementViewed({
              webElement: {
                elementType: MODAL_ELEMENT_TYPE,
                location: MODAL_LOCATION,
                name: programVariationName,
              },
            });
            observer.disconnect();
          }
        });
      },
      { threshold: MODAL_VIEWED_INTERSECTION_THRESHOLD },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [state.isDrawerOpen, hasContent, programVariationName]);

  const firstEntrySurfaceToken = useMemo(() => getFirstContentEntrySurfaceToken(content?.[0]), [content]);

  useEffect(() => {
    if (isDesktop && banner) {
      document.body.style.paddingRight = BANNER_OFFSET_PX;
    } else {
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.paddingRight = "";
    };
  }, [isDesktop, banner]);

  useEffect(() => {
    if (state.isDrawerOpen && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [state.isDrawerOpen]);

  useEffect(() => {
    if (!isDesktop || !state.isDrawerOpen) return;

    const reset = () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = banner ? BANNER_OFFSET_PX : "";
      document.documentElement.style.overflow = "";
    };

    reset();
    const frame = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(frame);
  }, [isDesktop, state.isDrawerOpen, banner]);

  useEffect(() => {
    if (!state.isDrawerOpen) return;
    const dialog = drawerContentRef.current?.closest("dialog");
    if (!dialog) return;

    const ignoreScrollbarClick = (event: MouseEvent) => {
      if (event.target !== dialog) return;
      const { left, right, top, bottom } = dialog.getBoundingClientRect();
      const clickedInsideDialogBounds = event.clientX >= left && event.clientX <= right && event.clientY >= top && event.clientY <= bottom;
      if (clickedInsideDialogBounds) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener("mousedown", ignoreScrollbarClick, true);
    return () => document.removeEventListener("mousedown", ignoreScrollbarClick, true);
  }, [state.isDrawerOpen]);

  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (auto_open_drawer && hasContent && !state.isDrawerOpen) {
      hasAutoOpened.current = true;
      dispatch({ type: "OPEN_DRAWER" });
    }
  }, [auto_open_drawer, hasContent, state.isDrawerOpen, dispatch]);

  useEffect(() => {
    if (state.isDrawerOpen && !hasContent) {
      dispatch({ type: "CLOSE_DRAWER" });
    }
  }, [state.isDrawerOpen, hasContent, dispatch]);

  const fireCloseButtonClicked = useCallback((position: "button" | "background") => {
    EventClient.elementClicked({
      webElement: {
        elementType: MODAL_CLOSE_ELEMENT_TYPE,
        htmlId: position,
        location: MODAL_LOCATION,
        position,
        text: "Close",
      },
    });
  }, []);

  const closeDrawer = useCallback(() => {
    fireCloseButtonClicked("background");
    dispatch({ type: "CLOSE_DRAWER" });
  }, [dispatch, fireCloseButtonClicked]);

 
  useEffect(() => {
    if (!state.isDrawerOpen) return;
    const dialog = drawerContentRef.current?.closest("dialog");
    if (!dialog) return;

    const onCancel = (event: Event) => {
      event.preventDefault();
      closeDrawer();
    };

    dialog.addEventListener("cancel", onCancel);
    return () => dialog.removeEventListener("cancel", onCancel);
  }, [state.isDrawerOpen, closeDrawer]);

  const handleMultiProgramClose = useCallback(() => {
    fireCloseButtonClicked("button");
    dispatch({ type: "CLOSE_DRAWER" });
  }, [dispatch, fireCloseButtonClicked]);

  const handleSingleProgramClose = useCallback(() => {
    if (closeButton?.fields?.action === "CLOSE_DRAWER") {
      fireCloseButtonClicked("button");
      dispatch({ type: closeButton.fields.action });
    }
  }, [dispatch, closeButton?.fields?.action, fireCloseButtonClicked]);

  const bannerType = banner?.sys?.contentType?.sys?.id;

  const closeBarStyle = useMemo(() => {
    if (!firstEntrySurfaceToken) return undefined;
    return { "--surface-background-color": `var(${firstEntrySurfaceToken})` } as React.CSSProperties;
  }, [firstEntrySurfaceToken]);

  const { bottomDrawerCloseSlot, sideDrawerCloseSlot } = useMemo(() => {
    const build = (variant: "bottom" | "side") => {
      const isSide = variant === "side";
      const placement = isSide ? ("left" as const) : ("center" as const);
      const sideGroupProps = isSide ? ({ layout: "inline" as const } as const) : {};
      const multiProgramBreakpoints = { md: { placement: "right" as const, layout: "inline" as const } };

      if (state.drawerType === "multi-program" || (!closeButton?.fields && children)) {
        return (
          <Container>
            <ButtonGroup
              className={styles.multiProgramClose}
              placement={placement}
              {...sideGroupProps}
              {...(!isSide ? { breakpoints: multiProgramBreakpoints } : {})}
            >
              <Button v2 size="large" variant="plain" iconSelection={{ icon: "x" }} onClick={handleMultiProgramClose} location={drawerLocation}>
                Close
              </Button>
            </ButtonGroup>
          </Container>
        );
      }

      if (closeButton && closeButton?.fields) {
        return (
          <ButtonGroup placement={placement} className={styles.buttonWrapper} {...sideGroupProps}>
            <Button
              variant={closeButton?.fields?.variation}
              color={closeButton?.fields?.color}
              size={closeButton?.fields?.size}
              iconSelection={closeButton?.fields?.iconSelection}
              onClick={handleSingleProgramClose}
              location={drawerLocation}
            >
              {closeButton?.fields?.label}
            </Button>
          </ButtonGroup>
        );
      }

      return null;
    };

    return {
      bottomDrawerCloseSlot: build("bottom"),
      sideDrawerCloseSlot: build("side"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.drawerType, closeButton?.fields, children, handleMultiProgramClose, handleSingleProgramClose, dispatch, drawerLocation]);

  const activeCloseSlot = isDesktop ? sideDrawerCloseSlot : bottomDrawerCloseSlot;

  const bannerElement = useMemo(() => {
    if (!banner) return null;
    if (bannerType === "banner") {
      return (
        <Banner
          className={cn(styles.banner, { [styles.sideDrawerBanner]: isDesktop })}
          {...(banner as TypeBannerWithoutUnresolvableLinksResponse)?.fields}
          inspector={inspector}
          phoneNumber={phoneNumber}
          location={drawerLocation}
          stripFocusContext="banner"
        />
      );
    }
    if (bannerType === "automated_content") {
      return (
        <AutomatedContentBanner
          className={cn(styles.banner, { [styles.sideDrawerBanner]: isDesktop })}
          {...banner?.fields}
          phoneNumber={phoneNumber}
          location={drawerLocation}
          stripFocusContext="banner"
        />
      );
    }
    return null;
  }, [banner, bannerType, isDesktop, inspector, phoneNumber, drawerLocation]);

  const renderContentEntry = useCallback(
    (entry: NonNullable<typeof content>[number]) => {
      if (!entry?.sys?.contentType?.sys?.id) return null;
      const id = entry.sys.id;
      const contentTypeId = entry.sys.contentType.sys.id;
      const sharedProps = { inspector, phoneNumber, isLoading };

      switch (contentTypeId) {
        case "form":
          return (
            <Form
              key={id}
              id={id}
              {...(entry as TypeFormWithoutUnresolvableLinksResponse).fields}
              inDrawer
              location={drawerLocation}
              {...sharedProps}
            />
          );
        case "hero":
          return <Hero key={id} id={id} {...(entry as TypeHeroWithoutUnresolvableLinksResponse).fields} {...sharedProps} programLogo={undefined} />;
        case "banner":
          return <Banner key={id} id={id} {...(entry as TypeBannerWithoutUnresolvableLinksResponse).fields} {...sharedProps} location={drawerLocation} />;
        case "automated_content":
          return <AutomatedContentBanner key={id} id={id} {...(entry as TypeAutomated_contentWithoutUnresolvableLinksResponse).fields} phoneNumber={phoneNumber} location={drawerLocation} />;
        case "accordion":
          return <Accordion key={id} id={id} {...(entry as TypeAccordionWithoutUnresolvableLinksResponse).fields} {...sharedProps} sectionName={drawerLocation} />;
        case "bento":
          return <Bento key={id} id={id} {...(entry as TypeBentoWithoutUnresolvableLinksResponse).fields} {...sharedProps} />;
        case "carousel":
          return <Carousel key={id} id={id} {...(entry as TypeCarouselWithoutUnresolvableLinksResponse).fields} {...sharedProps} mode="single-program" />;
        case "flex":
          return <Flex key={id} id={id} {...(entry as TypeFlexWithoutUnresolvableLinksResponse).fields} {...sharedProps} />;
        case "split":
          return <Split key={id} id={id} {...(entry as TypeSplitWithoutUnresolvableLinksResponse).fields} {...sharedProps} mode="single-program" />;
        case "tabs":
          return <Tabs key={id} id={id} {...(entry as TypeTabsWithoutUnresolvableLinksResponse).fields} {...sharedProps} sectionName={drawerLocation} programLogo={undefined} />;
        case "video":
          return <VideoEmbed key={id} {...(entry as TypeVideoWithoutUnresolvableLinksResponse).fields} />;
        default:
          return null;
      }
    },
    [inspector, phoneNumber, isLoading, drawerLocation],
  );

  return (
    <Fragment>
      {!isDesktop && bannerElement}
      {hasContent && (
        <BricksDrawer
          className={cn(styles.drawer, { [styles.sideDrawer]: isDesktop })}
          isOpen={state.isDrawerOpen}
          onClose={closeDrawer}
          position={drawerPosition}
        >
          <div ref={drawerContentRef} className={cn(styles.sideDrawerContentWrapper, { [styles.sideDrawerContent]: isDesktop })}>
            {activeCloseSlot && (
              <div className={styles.drawerCloseBar} style={closeBarStyle}>
                {activeCloseSlot}
              </div>
            )}
            {children ? (
              children
            ) : (
              content?.map((entry) => renderContentEntry(entry))
            )}
          </div>
        </BricksDrawer>
      )}
      {isDesktop && bannerElement}
    </Fragment>
  );
};
